import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useReducedMotion } from 'motion/react'
import { Aurora } from '@/components/motion/aurora'
import { RouteTransition } from '@/components/motion/enter'
import { SearchCommand } from './search-command'
import { RAIL_WIDTH, RAIL_WIDTH_COLLAPSED, Sidebar } from './sidebar'
import { Topbar } from './topbar'
import { labelForPath, recordRecentPage } from './recent-pages'
import { ensureShellStyles } from './shell-css'
import { useMediaQuery } from './use-media-query'

/**
 * App shell v2 — grouped sidenav + topbar chrome around the route outlet.
 * Desktop (≥1024px): fixed rail, collapsible to a 64px icon rail.
 * Below 1024px: the rail becomes a slide-in overlay behind a hamburger.
 * Mounts the global ⌘K command palette.
 */

ensureShellStyles()

const COLLAPSE_KEY = 'it.navCollapsed'

/**
 * Cap the routed page area on wide monitors so content doesn't stretch
 * full-bleed. The topbar/sidebar stay full-width (separate grid areas); only
 * this inner column is capped + centered, inside the gutters that `<main>`
 * provides via padding.
 */
const CONTENT_MAX_WIDTH = 1360

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

export interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const reducedMotion = useReducedMotion() ?? false
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const { location } = useRouterState()

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        // Persistence is best-effort.
      }
      return next
    })
  }, [])

  // ⌘K / Ctrl-K toggles the command palette; "/" opens it (unless you're
  // already typing into a field — including the palette's own input).
  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null): boolean => {
      const el = t as HTMLElement | null
      if (!el || !el.tagName) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((open) => !open)
        return
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Route change: close the overlay nav and remember the page for ⌘K.
  useEffect(() => {
    setMobileNavOpen(false)
    recordRecentPage({ label: labelForPath(location.pathname), path: location.pathname })
  }, [location.pathname])

  const railWidth = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isDesktop ? `${railWidth}px minmax(0, 1fr)` : 'minmax(0, 1fr)',
        gridTemplateRows: '56px 1fr',
        gridTemplateAreas: isDesktop
          ? '"rail topbar" "rail main"'
          : '"topbar" "main"',
        height: '100vh',
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: 'var(--ff-ui)',
        transition: reducedMotion
          ? 'none'
          : 'grid-template-columns var(--dur-base) var(--ease-out)',
      }}
    >
      <a href="#main-content" className="it-skip-link">
        Skip to main content
      </a>

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        overlay={!isDesktop}
        open={mobileNavOpen}
        reducedMotion={reducedMotion}
      />
      {!isDesktop && mobileNavOpen && (
        <div
          aria-hidden
          onClick={() => setMobileNavOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            background: 'color-mix(in srgb, var(--text) 32%, transparent)',
          }}
        />
      )}

      <Topbar
        showHamburger={!isDesktop}
        onToggleMobileNav={() => setMobileNavOpen((open) => !open)}
        onOpenPalette={() => setPaletteOpen(true)}
      />

      <main
        id="main-content"
        tabIndex={-1}
        style={{
          position: 'relative',
          gridArea: 'main',
          minWidth: 0,
          overflow: 'auto',
          padding: '24px 28px',
          outline: 'none',
        }}
      >
        {/* Soft aurora wash behind every page header; fades out down the page so
            there's no hard clipped edge. Purely decorative chrome. */}
        <Aurora
          style={{
            bottom: 'auto',
            height: 440,
            opacity: 0.07,
            maskImage: 'linear-gradient(to bottom, #000 35%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 35%, transparent 100%)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto' }}>
          <RouteTransition routeKey={location.pathname}>{children}</RouteTransition>
        </div>
      </main>

      <SearchCommand open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
